import React from 'react'
import { Card, CardContent, Badge, cn } from '@wove/web-ui'
import { AlertCircle } from 'lucide-react'

interface CrossValidationResultsProps {
  results: any
  documents?: any[]
}

export const CrossValidationResults: React.FC<CrossValidationResultsProps> = ({
  results,
  documents = [],
}) => {
  console.log('CrossValidationResults - received results:', results)

  if (!results) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No validation results available</p>
      </div>
    )
  }

  // Handle both formats - direct API response and subscription response
  let validatedFields = []
  let issues = []
  let validatedFieldsMap: Record<string, string> = {}

  if (results.validatedFields && results.issues) {
    // Direct API format
    validatedFields = results.validatedFields || []
    issues = results.issues || []
    validatedFieldsMap = results.validatedFieldsMap || {}
  } else if (results.validationResult && Array.isArray(results.validationResult)) {
    // Subscription format - process the validation items
    validatedFields = []
    issues = []

    results.validationResult.forEach((item: any) => {
      if (item.status === 'validated') {
        validatedFields.push(item.field)
        if (item.reason) {
          validatedFieldsMap[item.field] = item.reason
        }
      } else {
        issues.push({
          field: item.field,
          message: item.reason || '',
          severity: item.status === 'critical' ? 'critical' : 'warning',
          suggestion: item.suggestion,
          files: item.files || [],
          documents: item.documentIds || [],
          values: item.values,
        })
      }
    })
  }

  console.log('CrossValidationResults - processed:', { validatedFields, issues })

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-green-600">{validatedFields.length}</p>
            <p className="text-sm text-gray-600">Validated Fields</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-red-600">
              {issues.filter((i: any) => i.severity === 'critical').length}
            </p>
            <p className="text-sm text-gray-600">Critical Issues</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-600">
              {issues.filter((i: any) => i.severity === 'warning').length}
            </p>
            <p className="text-sm text-gray-600">Warnings</p>
          </div>
        </div>
      </div>

      {/* Validated Fields */}
      {validatedFields.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">✅ Validated Fields</h3>
          <div className="space-y-3">
            {validatedFields.map((field: string) => (
              <div key={field} className="flex items-start gap-3">
                <Badge variant="success" className="flex-shrink-0">
                  {field}
                </Badge>
                <span className="text-sm text-gray-600">
                  {validatedFieldsMap[field] || 'Field validated successfully'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issues */}
      {issues.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Issues Found</h3>
          <div className="space-y-3">
            {issues.map((issue: any, index: number) => (
              <Card
                key={index}
                className={cn(
                  'border-l-4',
                  issue.severity === 'critical' ? 'border-red-500' : 'border-amber-500',
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {issue.severity === 'critical' ? (
                        <AlertCircle className="text-red-500" size={20} />
                      ) : (
                        <AlertCircle className="text-amber-500" size={20} />
                      )}
                      <span className="font-medium">{issue.field}</span>
                      <Badge variant={issue.severity === 'critical' ? 'error' : 'warning'}>
                        {issue.severity}
                      </Badge>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-2">{issue.message}</p>

                  {issue.documents && documents.length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs font-medium text-gray-500">Affected documents:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {issue.documents.map((docId: string) => {
                          const doc = documents.find((d) => d.id === docId)
                          return (
                            <span key={docId} className="text-xs bg-gray-100 px-2 py-1 rounded">
                              {doc?.name || docId}
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {issue.values && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                      <span className="font-medium">Found values:</span>
                      <div className="mt-1 space-y-1">
                        {Object.entries(issue.values).map(([docId, value]) => {
                          const doc = documents.find((d) => d.id === docId)
                          return (
                            <div key={docId}>
                              <span className="text-gray-600">{doc?.name || docId}:</span>{' '}
                              <span className="font-mono">{String(value)}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* If files array exists but values doesn't, show the files */}
                  {!issue.values && issue.documents && issue.documents.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      <span className="font-medium">Files:</span> {issue.documents.join(', ')}
                    </div>
                  )}

                  {issue.suggestion && (
                    <div className="mt-2 text-sm text-blue-600">💡 {issue.suggestion}</div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
